/**
 * React hooks for WooCommerce headless integration.
 *
 * Falls back to Supabase products table when WC is not configured.
 */

import { useQuery } from '@tanstack/react-query';
import { wcProducts, wcCategories, WC_ENABLED } from '@/integrations/woo';
import type { WCProduct, WCCategory, WCListParams } from '@/integrations/woo';
import { pmsFrom } from '@/integrations/pms';

/** Map a Supabase product row → WCProduct-like shape for unified rendering */
const mapSupabaseProduct = (row: any): WCProduct => ({
  id: row.id,
  name: row.name,
  slug: row.slug,
  type: 'simple',
  status: row.is_active ? 'publish' : 'draft',
  featured: row.is_featured ?? false,
  description: row.description ?? '',
  short_description: row.short_description ?? '',
  sku: row.sku ?? '',
  price: row.price ? String(row.price) : '',
  regular_price: row.compare_at_price ? String(row.compare_at_price) : '',
  sale_price: row.price && row.compare_at_price ? String(row.price) : '',
  on_sale: !!(row.price && row.compare_at_price && Number(row.price) < Number(row.compare_at_price)),
  purchasable: true,
  in_stock: row.in_stock ?? true,
  stock_quantity: null,
  categories: row.category_id ? [{ id: row.category_id, name: '', slug: '' }] : [],
  tags: [],
  images: Array.isArray(row.images) ? row.images.map((src: string, i: number) => ({ id: i, src, name: '', alt: row.name })) : [],
  attributes: [],
  variations: [],
  meta_data: [],
  related_ids: [],
});

export const useWCProducts = (params?: WCListParams) => {
  return useQuery({
    queryKey: ['wc-products', params],
    queryFn: async () => {
      if (WC_ENABLED) {
        const result = await wcProducts.getList(params);
        return result;
      }
      // Fallback to Supabase products
      let query = pmsFrom('products')
        .select('*', { count: 'exact' })
        .eq('is_active', true);

      if (params?.featured) query = query.eq('is_featured', true);
      if (params?.search) query = query.ilike('name', `%${params.search}%`);
      if (params?.per_page) query = query.limit(params.per_page);
      if (params?.on_sale) query = query.not('compare_at_price', 'is', null);

      query = query.order('is_featured', { ascending: false }).order('sort_order');

      const { data, count, error } = await query;
      if (error) throw error;

      return {
        data: (data || []).map(mapSupabaseProduct),
        total: count || 0,
        totalPages: 1,
      };
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useWCProduct = (slugOrId: string | number) => {
  return useQuery({
    queryKey: ['wc-product', slugOrId],
    queryFn: async () => {
      if (WC_ENABLED) {
        if (typeof slugOrId === 'number') {
          return await wcProducts.getOne(slugOrId);
        }
        return await wcProducts.getBySlug(String(slugOrId));
      }
      // Fallback to Supabase
      const { data, error } = await pmsFrom('products')
        .select('*')
        .eq('slug', String(slugOrId))
        .eq('is_active', true)
        .maybeSingle();
      if (error) throw error;
      return data ? mapSupabaseProduct(data) : null;
    },
    enabled: !!slugOrId,
  });
};

export const useWCCategories = () => {
  return useQuery({
    queryKey: ['wc-categories'],
    queryFn: async () => {
      if (WC_ENABLED) {
        return await wcCategories.getList();
      }
      // Fallback to Supabase
      const { data, error } = await pmsFrom('product_categories')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');
      if (error) throw error;
      return (data || []).map((c: any): WCCategory => ({
        id: c.id,
        name: c.name,
        slug: c.slug ?? c.name.toLowerCase().replace(/\s+/g, '-'),
        parent: 0,
        description: c.description ?? '',
        image: null,
        count: 0,
      }));
    },
    staleTime: 1000 * 60 * 10,
  });
};

export const useWCFeaturedProducts = (limit = 6) => {
  return useWCProducts({ featured: true, per_page: limit });
};

export const useWCOnSaleProducts = (limit = 6) => {
  return useWCProducts({ on_sale: true, per_page: limit });
};
